'use client';

import { useLocale } from 'next-intl';
import { RefObject, useCallback, useEffect, useRef } from 'react';

import { trackingManager } from '@/lib/tracking/tracking-manager-instance';

interface SectionVisibilityOptions {
  threshold?: number;
  // Allows reusing an existing ref (e.g. when the section already needs a ref
  // for framer-motion useScroll). Without this, callers wire two refs to the
  // same DOM node manually, which is fragile.
  ref?: RefObject<HTMLElement | null>;
}

// Page load time reference for calculating timeToView
const pageLoadTime = typeof window !== 'undefined' ? Date.now() : 0;

// Track which sections have been viewed in this session
const viewedSections = new Set<string>();

export function useSectionVisibility(sectionId: string, options: SectionVisibilityOptions = {}) {
  const { threshold = 0.5, ref: externalRef } = options;
  const locale = useLocale();
  const internalRef = useRef<HTMLElement>(null);
  const ref = externalRef ?? internalRef;
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Skip if already tracked in this session
    if (viewedSections.has(sectionId) || hasTrackedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true;
          viewedSections.add(sectionId);

          const timeToView = Math.round((Date.now() - pageLoadTime) / 1000);
          const viewportPercentage = Math.round(entry.intersectionRatio * 100);

          trackingManager.trackEvent('LANDING_SECTION_VIEWED', {
            section: sectionId,
            viewportPercentage,
            timeToView,
            language: locale,
          });

          // Clean up after tracking
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [sectionId, threshold, locale, ref]);

  return { ref };
}

// Track scroll depth milestones (25%, 50%, 75%, 100%)
const trackedMilestones = new Set<number>();

export function useScrollDepthTracking() {
  const locale = useLocale();
  const startTimeRef = useRef<number>(Date.now());

  const trackScrollDepth = useCallback(
    (depth: number, maxDepth: number) => {
      if (trackedMilestones.has(depth)) return;

      trackedMilestones.add(depth);
      const timeToReach = Math.round((Date.now() - startTimeRef.current) / 1000);

      trackingManager.trackEvent('LANDING_SCROLL_DEPTH_REACHED', {
        depth,
        maxDepth,
        timeToReach,
        language: locale,
      });
    },
    [locale],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;

      if (docHeight <= 0) return;

      const scrollPercentage = Math.round((scrollTop / docHeight) * 100);
      const milestones = [25, 50, 75, 100];

      for (const milestone of milestones) {
        if (scrollPercentage >= milestone && !trackedMilestones.has(milestone)) {
          trackScrollDepth(milestone, scrollPercentage);
        }
      }
    };

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackScrollDepth]);
}
